import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, AlertTriangle } from "lucide-react";
import { submitEnquiry, getBlockedDates } from "@/lib/api";
import { PROPERTIES } from "@/data/site";
import { formatRange, isPastRange, rangesClash } from "@/lib/dates";

const inputCls =
  "w-full rounded-none border-0 border-b border-[#0F2A52]/25 bg-transparent px-0 py-3 text-sm text-[#1A1A1A] placeholder:text-[#4A4A4A]/50 focus:outline-none focus:border-[#0F2A52] transition-colors duration-200";

export const EnquiryForm = ({ propertySlug = "", compact = false }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    property_slug: propertySlug,
    check_in: "",
    check_out: "",
    guests: 2,
    message: "",
  });

  const [blocked, setBlocked] = useState([]);

  useEffect(() => {
    if (!form.property_slug) {
      setBlocked([]);
      return;
    }
    getBlockedDates(form.property_slug)
      .then((r) => setBlocked(r.data.filter((x) => !isPastRange(x.end))))
      .catch(() => setBlocked([]));
  }, [form.property_slug]);

  const clashes = form.check_in
    ? blocked.filter((b) => rangesClash(form.check_in, form.check_out, b.start, b.end))
    : [];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so Robert can reply.");
      return;
    }
    setSending(true);
    try {
      const chosen = PROPERTIES.find((p) => p.slug === form.property_slug);
      await submitEnquiry({
        ...form,
        guests: Number(form.guests) || 1,
        property_name: chosen ? chosen.name : "General enquiry",
      });
      setSent(true);
      toast.success("Enquiry sent. Robert will be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please call or email instead.");
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="border border-[#0F2A52]/15 p-8 bg-[#F2EBDD]" data-testid="enquiry-success">
        <h3 className="font-display text-3xl text-[#0F2A52]">Thank you</h3>
        <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3">
          Your enquiry has been received. Robert normally replies the same day, and will confirm availability and
          the exact rate for your dates.
        </p>
        <button
          onClick={() => setSent(false)}
          className="mt-6 text-sm text-[#B08A3E] hover:text-[#0F2A52] transition-colors duration-200"
          data-testid="enquiry-send-another"
        >
          Send another enquiry
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-6" data-testid="enquiry-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Name</label>
          <input className={inputCls} value={form.name} onChange={set("name")} data-testid="enquiry-name" required />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Email</label>
          <input
            type="email"
            className={inputCls}
            value={form.email}
            onChange={set("email")}
            data-testid="enquiry-email"
            required
          />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Phone</label>
          <input className={inputCls} value={form.phone} onChange={set("phone")} data-testid="enquiry-phone" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Guests</label>
          <input
            type="number"
            min="1"
            max="12"
            className={inputCls}
            value={form.guests}
            onChange={set("guests")}
            data-testid="enquiry-guests"
          />
        </div>
      </div>

      {!compact && (
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Property</label>
          <select
            className={inputCls}
            value={form.property_slug}
            onChange={set("property_slug")}
            data-testid="enquiry-property"
          >
            <option value="">Not sure yet / general enquiry</option>
            {PROPERTIES.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Arrive</label>
          <input type="date" className={inputCls} value={form.check_in} onChange={set("check_in")} data-testid="enquiry-checkin" />
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Depart</label>
          <input type="date" className={inputCls} value={form.check_out} onChange={set("check_out")} data-testid="enquiry-checkout" />
        </div>
      </div>

      {clashes.length > 0 && (
        <div
          className="flex items-start gap-3 border border-[#B08A3E]/50 bg-[#F2EBDD] p-4"
          data-testid="enquiry-date-clash"
        >
          <AlertTriangle size={16} className="text-[#B08A3E] mt-0.5 shrink-0" />
          <p className="text-xs text-[#4A4A4A] leading-relaxed">
            Those dates overlap nights that are already booked
            {clashes.length === 1 ? ` (${formatRange(clashes[0].start, clashes[0].end)})` : ""}. You are very
            welcome to send the enquiry anyway — Robert will reply with the nearest dates he has free.
          </p>
        </div>
      )}

      <div>
        <label className="block text-[11px] uppercase tracking-[0.24em] text-[#4A4A4A] mb-2">Message</label>
        <textarea
          rows={4}
          className={inputCls + " resize-none"}
          value={form.message}
          onChange={set("message")}
          placeholder="Who's travelling, anything you'd like to know…"
          data-testid="enquiry-message"
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        className="inline-flex items-center gap-3 bg-[#0F2A52] text-[#FCFAF6] px-8 py-4 text-sm hover:-translate-y-1 hover:shadow-lg transition-transform duration-200 disabled:opacity-60"
        data-testid="enquiry-submit"
      >
        {sending && <Loader2 size={16} className="animate-spin" />}
        {sending ? "Sending" : "Send enquiry"}
      </button>
      <p className="text-xs text-[#0F2A52] leading-relaxed" data-testid="enquiry-reply-time">
        Robert usually replies the same day, and always from his own email — never an automated system.
      </p>
      <p className="text-xs text-[#4A4A4A]/80 leading-relaxed">
        No payment is taken online. Robert confirms availability, the total price and payment details with you
        directly.
      </p>
    </form>
  );
};

export default EnquiryForm;
